// src/config/database/lfRollerBannersConfig.js

export const lfRollerBannersTableConfig = {
  // Table identification
  id: 'lf_roller_banners',
  name: 'Large Format Roller Banners',
  apiEndpoint: '/api/admin/large-format/roller-banners',
  
  // Column definitions for the DataTable
  columns: [
    {
      key: 'name',
      label: 'Banner Name',
      sortable: true,
      width: '25%',
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      )
    },
    {
      key: 'dimensions',
      label: 'Dimensions',
      sortable: false,
      width: '15%',
      render: (value, record) => {
        const width = record.width_mm ? `${record.width_mm}mm` : '-';
        const height = record.height_mm ? `${record.height_mm}mm` : '-';
        
        return (
          <div className="text-sm text-gray-700">
            <div>{width} × {height}</div>
            {record.width_mm && record.height_mm && (
              <div className="text-xs text-gray-500">
                {((record.width_mm * record.height_mm) / 1000000).toFixed(2)}m²
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'price',
      label: 'Base Price',
      sortable: true,
      width: '12%',
      render: (value) => (
        <span className="text-green-600 font-medium">
          {value ? `£${parseFloat(value).toFixed(2)}` : '-'}
        </span>
      )
    },
    {
      key: 'markup_percent',
      label: 'Trade Markup %',
      sortable: true,
      width: '12%',
      render: (value) => (
        <span className="text-blue-600 font-medium">
          {value ? `${parseFloat(value).toFixed(0)}%` : '0%'}
        </span>
      )
    },
    {
      key: 'retail_markup_percent',
      label: 'Retail Markup %',
      sortable: true,
      width: '12%',
      render: (value) => (
        <span className="text-purple-600 font-medium">
          {value ? `${parseFloat(value).toFixed(0)}%` : '-'}
        </span>
      )
    },
    {
      key: 'final_price',
      label: 'Trade Price',
      sortable: false,
      width: '12%',
      render: (value, record) => {
        if (!record.price) return <span className="text-gray-400">-</span>;
        
        const basePrice = parseFloat(record.price);
        const markup = parseFloat(record.markup_percent || 0);
        const finalPrice = basePrice + (basePrice * markup / 100);
        
        return (
          <span className="text-blue-600 font-medium">
            £{finalPrice.toFixed(2)}
          </span>
        );
      }
    },
    {
      key: 'detail',
      label: 'Details',
      sortable: true,
      width: '12%',
      render: (value) => (
        <span className="text-gray-600 text-sm truncate max-w-xs" title={value}>
          {value || '-'}
        </span>
      )
    }
  ],

  // Field definitions for the RecordModal form
  fields: [
    {
      key: 'name',
      label: 'Banner Name',
      type: 'text',
      required: true,
      placeholder: 'e.g., Premium 850mm Roller Banner, Budget Pull-up Banner',
      help: 'A descriptive name for this roller banner type. The ID will be generated automatically.'
    },
    {
      key: 'detail',
      label: 'Details',
      type: 'textarea',
      required: false,
      placeholder: 'Additional details about this roller banner...',
      rows: 2,
      help: 'Optional detailed description of the banner specifications'
    },
    {
      key: 'width_mm',
      label: 'Width (mm)',
      type: 'number',
      required: false,
      min: 0,
      max: 3000,
      placeholder: '850',
      help: 'Width of the banner in millimeters (e.g., 850mm, 1000mm, 1200mm)'
    },
    {
      key: 'height_mm',
      label: 'Height (mm)',
      type: 'number',
      required: false,
      min: 0,
      max: 5000,
      placeholder: '2000',
      help: 'Height of the banner in millimeters (e.g., 2000mm, 2200mm)'
    },
    {
      key: 'price',
      label: 'Base Price (£)',
      type: 'number',
      required: false,
      step: 0.01,
      min: 0,
      placeholder: '45.00',
      help: 'Base cost price before markup (cost to produce/purchase)'
    },
    {
      key: 'markup_percent',
      label: 'Markup Percentage (%)',
      type: 'number',
      required: false,
      min: 0,
      defaultValue: '100',
      placeholder: '100',
      help: 'Trade tier markup percentage applied to base price (e.g., 100% doubles the price)'
    },
    {
      key: 'retail_markup_percent',
      label: 'Retail Markup Percentage (%)',
      type: 'number',
      required: false,
      min: 0,
      placeholder: '150',
      help: 'Retail tier markup percentage applied to base price'
    }
  ]
};

// Transform functions for API communication
export const transformLfRollerBannerForApi = (formData) => {
  const transformed = {
    name: formData.name
  };
  
  // Only include fields if they have values
  if (formData.detail && formData.detail.trim() !== '') {
    transformed.detail = formData.detail;
  }
  
  if (formData.width_mm && formData.width_mm !== '' && !isNaN(parseFloat(formData.width_mm))) {
    transformed.width_mm = parseFloat(formData.width_mm);
  }
  
  if (formData.height_mm && formData.height_mm !== '' && !isNaN(parseFloat(formData.height_mm))) {
    transformed.height_mm = parseFloat(formData.height_mm);
  }
  
  if (formData.price && formData.price !== '' && !isNaN(parseFloat(formData.price))) {
    transformed.price = parseFloat(formData.price);
  }
  
  if (formData.markup_percent && formData.markup_percent !== '' && !isNaN(parseFloat(formData.markup_percent))) {
    transformed.markup_percent = parseFloat(formData.markup_percent);
  }
  
  if (formData.retail_markup_percent && formData.retail_markup_percent !== '' && !isNaN(parseFloat(formData.retail_markup_percent))) {
    transformed.retail_markup_percent = parseFloat(formData.retail_markup_percent);
  }
  
  return transformed;
};

export const transformLfRollerBannerFromApi = (apiData) => {
  return {
    id: apiData.id,
    name: apiData.name || '',
    detail: apiData.detail || '',
    width_mm: apiData.width_mm ? apiData.width_mm.toString() : '',
    height_mm: apiData.height_mm ? apiData.height_mm.toString() : '',
    price: apiData.price ? apiData.price.toString() : '',
    markup_percent: apiData.markup_percent ? apiData.markup_percent.toString() : '100',
    retail_markup_percent: apiData.retail_markup_percent ? apiData.retail_markup_percent.toString() : ''
  };
};

// Validation rules
export const validateLfRollerBanner = (data) => {
  const errors = {};
  
  if (!data.name || data.name.trim() === '') {
    errors.name = 'Banner name is required';
  } else if (data.name.trim().length < 2) {
    errors.name = 'Banner name must be at least 2 characters long';
  }
  
  // Validate numeric fields
  const numericFields = ['width_mm', 'height_mm', 'price', 'markup_percent', 'retail_markup_percent'];
  
  numericFields.forEach(field => {
    if (data[field] && data[field] !== '') {
      const value = parseFloat(data[field]);
      if (isNaN(value) || value < 0) {
        errors[field] = `${field.replace(/_/g, ' ')} must be a non-negative number`;
      }
    }
  });
  
  // Specific validation for dimensions
  if (data.width_mm && parseFloat(data.width_mm) > 3000) {
    errors.width_mm = 'Width seems unusually large (max 3000mm). Please verify.';
  }
  
  if (data.height_mm && parseFloat(data.height_mm) > 5000) {
    errors.height_mm = 'Height seems unusually large (max 5000mm). Please verify.';
  }
  
  // Check that at least price is provided for meaningful banner
  if (!data.price || parseFloat(data.price) <= 0) {
    errors.price = 'Base price must be provided and greater than 0';
  }
  
  return Object.keys(errors).length > 0 ? errors : null;
};

// Helper function to calculate final price
export const calculateFinalPrice = (basePrice, markupPercent) => {
  if (!basePrice || isNaN(basePrice)) return 0;
  const markup = parseFloat(markupPercent || 0);
  return parseFloat(basePrice) + (parseFloat(basePrice) * markup / 100);
};

// Helper function to calculate retail price
export const calculateRetailPrice = (basePrice, retailMarkupPercent) => {
  if (!basePrice || isNaN(basePrice)) return 0;
  const markup = parseFloat(retailMarkupPercent || 0);
  return parseFloat(basePrice) + (parseFloat(basePrice) * markup / 100);
};

// Common roller banner templates for quick creation
export const ROLLER_BANNER_TEMPLATES = [
  {
    name: 'Standard 850mm Roller Banner',
    width_mm: '850',
    height_mm: '2000',
    price: '35.00',
    markup_percent: '100',
    retail_markup_percent: '150',
    detail: 'Standard pull-up banner with aluminum base'
  },
  {
    name: 'Premium 850mm Roller Banner',
    width_mm: '850', 
    height_mm: '2000',
    price: '45.00',
    markup_percent: '100',
    retail_markup_percent: '150',
    detail: 'Premium quality with enhanced graphics and stable base'
  },
  {
    name: 'Wide 1000mm Roller Banner',
    width_mm: '1000',
    height_mm: '2000',
    price: '42.00',
    markup_percent: '100',
    retail_markup_percent: '150',
    detail: 'Wide format roller banner for maximum impact'
  },
  {
    name: 'Extra Wide 1200mm Roller Banner',
    width_mm: '1200',
    height_mm: '2000',
    price: '55.00',
    markup_percent: '100',
    retail_markup_percent: '150',
    detail: 'Extra wide format for large displays'
  },
  {
    name: 'Tall 850mm Roller Banner',
    width_mm: '850',
    height_mm: '2200',
    price: '38.00',
    markup_percent: '100',
    retail_markup_percent: '150',
    detail: 'Taller banner for increased visibility'
  },
  {
    name: 'Budget 800mm Roller Banner',
    width_mm: '800',
    height_mm: '2000',
    price: '25.00',
    markup_percent: '120',
    retail_markup_percent: '180',
    detail: 'Economy option for budget-conscious customers'
  }
];

export default lfRollerBannersTableConfig;